/**
 * Notification utilities using Supabase Realtime
 */

import { supabase, subscribeWithAuth } from './supabaseClient';

/**
 * Get all notifications for the current user
 */
export const getNotifications = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching notifications:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { data: [], error };
  }
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { error };
  }
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { error };
  }
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) {
      console.error('Error getting unread count:', error);
      return { count: 0, error };
    }

    return { count: count || 0, error: null };
  } catch (error) {
    console.error('Error getting unread count:', error);
    return { count: 0, error };
  }
};

/**
 * Subscribe to real-time notifications for a user
 * @param {string} userId - User ID
 * @param {function} callback - Callback function that receives the notification
 * @returns {function} Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback) => {
  return subscribeWithAuth(`notifications:${userId}`, [
    {
      event: 'INSERT',
      table: 'notifications',
      filter: `user_id=eq.${userId}`,
      handler: (payload) => callback(payload.new),
    },
  ]);
};

/**
 * Delete/dismiss a notification
 */
export const dismissNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) {
      console.error('Error dismissing notification:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Error dismissing notification:', error);
    return { error };
  }
};

